/**
 * Escrita (§5.2.4): `RouteState` → hash canônico. Pura. Ordem fixa `#/` + cargo + lugar + `?` + `mapa` → `cand` → `tela` → `t`,
 * tudo em minúsculas; `tv` e os parâmetros de fonte de dados (§5.2.8) nunca são escritos.
 */
import type { CandidateNumber } from '../contracts/base';
import type { PlaceRoute, RouteState } from '../model/types';
import { ABROAD_SLUG, COMMENTS_SLUG, MODE_SLUGS, OFFICE_SLUGS, SCREEN_PARAM } from './slugs';
import type { RouteContext } from './types';

/** Segmentos do lugar, com a barra inicial ('' para o Brasil). A cidade do exterior sai sempre com o país. */
export function placePath(p: PlaceRoute): string {
  switch (p.kind) {
    case 'br':
      return '';
    case 'uf':
      return `/${p.uf.toLowerCase()}`;
    case 'municipality':
      return `/${p.uf.toLowerCase()}/${p.ibge}${p.zone != null ? `/z${p.zone}` : ''}`;
    case 'abroad':
      return `/${ABROAD_SLUG}`;
    case 'country':
      return `/${ABROAD_SLUG}/${p.iso2.toLowerCase()}`;
    case 'abroad_city':
      return `/${ABROAD_SLUG}/${p.iso2.toLowerCase()}/${p.tse}`;
  }
}

/** `t=HHMM` com `HH = floor(tMin / 60)` sem `% 24` (00h51 do dia 26 = "2451"). */
export function hhmm(tMin: number): string {
  const t = Math.max(0, Math.floor(tMin));
  return `${String(Math.floor(t / 60)).padStart(2, '0')}${String(t % 60).padStart(2, '0')}`;
}

/**
 * `cand` quando `mapCandidate = null`: o líder nacional do instante exibido; antes de haver votos, o candidato de menor
 * número (mesma regra de 13 à esquerda). null enquanto nenhum dos dois é conhecido (cadastro ainda não chegou).
 */
export function resolveDefaultCandidate(
  ctx: Pick<RouteContext, 'nationalLeader' | 'candidates'>,
): CandidateNumber | null {
  if (ctx.nationalLeader) return ctx.nationalLeader;
  const list: readonly { number: CandidateNumber }[] = ctx.candidates?.president ?? [];
  return list.map((c) => c.number).sort((a, b) => Number(a) - Number(b) || a.localeCompare(b))[0] ?? null;
}

/** §5.2.4. */
export function formatRoute(r: RouteState, ctx: Pick<RouteContext, 'nationalLeader' | 'candidates'>): string {
  const h = `#/${OFFICE_SLUGS[r.office]}${placePath(r.place)}`;
  const q: string[] = [];
  if (r.office === 'president' && r.mapMode !== 'municipalities') {
    q.push(`mapa=${MODE_SLUGS[r.mapMode]}`);
    if (r.mapMode === 'candidate') {
      const cand = r.mapCandidate ?? resolveDefaultCandidate(ctx);
      if (cand != null) q.push(`cand=${cand}`);
    }
  }
  if (r.screen === 'comments') q.push(`${SCREEN_PARAM}=${COMMENTS_SLUG}`);
  if (r.replayTMin != null) q.push(`t=${hhmm(r.replayTMin)}`);
  return q.length ? `${h}?${q.join('&')}` : h;
}

/** Hash com `tv=1` no fim (só para manter a TV numa recarga, §5.2.7; `formatRoute` nunca o escreve). */
export function withTvParam(hash: string): string {
  return `${hash}${hash.includes('?') ? '&' : '?'}tv=1`;
}
